import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import API, { apis } from "common/apis";
import { IUserTableRackMappingListAPI } from "common/interface/IUserTableRackMapping";
import { logoutIfNeeded } from "common/utils";

export interface UserTableRackMappingState {
  userTableRackMappings: IUserTableRackMappingListAPI;
  listStatus: "idle" | "loading" | "error";
  listError?: string;
  // Latest list request; responses from older requests are dropped.
  listRequestId?: string;
}

const emptyList: IUserTableRackMappingListAPI = {
  pageNo: 1,
  pageSize: 25,
  pages: 0,
  total: 0,
  data: [],
};

const initialState: UserTableRackMappingState = {
  userTableRackMappings: emptyList,
  listStatus: "idle",
};

const getApiErrorMessage = (error: any, fallback = "Something went wrong!") => {
  const data = error?.response?.data;
  if (data?.errors?.length) {
    return data.errors.map((e: { message?: string }) => e.message).filter(Boolean).join(", ");
  }
  return data?.message || fallback;
};

export const getUserTableRackMappings = createAsyncThunk<
  IUserTableRackMappingListAPI,
  { storeId: number | string; pageNo?: number; pageSize?: number; tableId?: number }
>("userTableRackMapping/list", async ({ storeId, pageNo = 1, pageSize = 25, tableId }, thunkApi) => {
  try {
    const response = await API.get(apis.getUserTableRackMappings, {
      params: { storeId, pageNo, pageSize, tableId },
    });
    return response.data as IUserTableRackMappingListAPI;
  } catch (error: any) {
    logoutIfNeeded(error, thunkApi);
    return thunkApi.rejectWithValue({ message: getApiErrorMessage(error) });
  }
});

const userTableRackMappingSlice = createSlice({
  name: "userTableRackMapping",
  initialState,
  reducers: {
    clearUserTableRackMappings: (state) => {
      state.userTableRackMappings = emptyList;
      state.listStatus = "idle";
      state.listError = undefined;
      state.listRequestId = undefined;
    },
  },
  extraReducers(builder) {
    builder
      .addCase(getUserTableRackMappings.pending, (state, action) => {
        state.listRequestId = action.meta.requestId;
        state.listStatus = "loading";
        state.listError = undefined;
      })
      .addCase(getUserTableRackMappings.fulfilled, (state, action) => {
        if (state.listRequestId !== action.meta.requestId) return;
        state.listStatus = "idle";
        state.userTableRackMappings = action.payload;
      })
      .addCase(getUserTableRackMappings.rejected, (state, action) => {
        if (state.listRequestId !== action.meta.requestId) return;
        state.listStatus = "error";
        state.listError = (action.payload as { message: string })?.message;
      });
  },
});

export const { clearUserTableRackMappings } = userTableRackMappingSlice.actions;
export default userTableRackMappingSlice.reducer;
